import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import styled from 'styled-components';

import { mq } from 'components/constants/breakpoints';

import { StudentColors } from 'components/pages/StudentDetail/parts/studentColors';
import {
  downloadRosterCsv,
  IRosterCsvHeaders,
  IRosterExportRow,
} from 'core/utils/exportCsv';
import { debtOf } from 'core/utils/studentRoster';
import type { IStudentListRow } from 'core/utils/studentRoster';

interface IRosterExportButtonProps {
  /** The currently visible rows (after filter + search + sort), Req 21.1. */
  rows: IStudentListRow[];
  /** Finance-view permission. When false the control is not rendered (Req 21.5). */
  canExportFinance: boolean;
}

const toNumber = (value: number | string | null | undefined): number => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Roster export control for the Students list (Area 8, Req 21.1/21.3/21.5).
 *
 * Maps the currently visible rows onto the shared `IRosterExportRow` extended
 * with the `group` column and downloads a CSV with localized headers (Req 21.4).
 * Hidden entirely when the operator lacks finance-view permission (Req 21.5);
 * the underlying builder additionally has no side effect when no rows exist.
 */
export const RosterExportButton = memo(
  ({ rows, canExportFinance }: IRosterExportButtonProps) => {
    const { t } = useTranslation();

    const onExport = useCallback(() => {
      const exportRows: IRosterExportRow[] = rows.map((row) => ({
        studentName: row.name,
        agreementTitle: row.contractTitle,
        group: row.groupName,
        currency: row.currency,
        budget: toNumber(row.budgetAmount),
        paid: toNumber(row.paidAmount),
        // The same value the screen shows (`debtOf` — server debt, zeroed for
        // terminated). No longer faked by feeding paid = budget so the builder
        // would derive 0.
        debt: debtOf(row),
      }));

      const headers: IRosterCsvHeaders = {
        studentName: t('table.students.name'),
        agreementTitle: t('table.students.contractTitle'),
        group: t('table.students.group'),
        currency: t('table.students.currency'),
        budget: t('table.students.budgetAmount'),
        paid: t('table.students.paidAmount'),
        debt: t('table.students.debt'),
      };

      downloadRosterCsv(exportRows, headers, 'students-roster.csv');
    }, [rows, t]);

    if (!canExportFinance) return null;

    return (
      <Button type="button" onClick={onExport}>
        <Download size={14} strokeWidth={2} />
        <span>{t('pages.students.export')}</span>
      </Button>
    );
  },
);

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${StudentColors.border};
  border-radius: 8px;
  background: ${StudentColors.surface};
  color: ${StudentColors.accent};
  font: inherit;
  font-size: 13px;
  padding: 7px 14px;
  cursor: pointer;
  white-space: nowrap;

  ${mq.mobile} {
    min-height: 40px;
  }

  &:hover {
    background: ${StudentColors.accentSurface};
  }
`;
